import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verifyFormToken, publicSubmitInfluencer } from '../api';
import { TIERS, NICHES, LANGUAGES, DELIVERABLES, CHORD, TIER_COLORS, tierFromFollowers } from '../constants';

const RATE_TYPES = ['stories', 'reels', 'shoot', 'static', 'affiliate'];

const EMPTY = {
  influencer_name:'', handle:'', tier:'Nano', niche:'', city:'', state:'', country:'India',
  language:'', audience:'', followers:'', avg_views:'', engagement_rate:'',
  email_contact:'', agency_manager:'', rate:'',
  preferred_deliverables:'', past_work:'', notes:'',
  rate_stories:'', rate_reels:'', rate_shoot:'', rate_static:'', rate_affiliate:'',
};

const inp = {
  background: 'rgba(255,255,255,0.06)',
  border: '0.5px solid rgba(255,255,255,0.15)',
  borderRadius: 8, padding: '10px 14px',
  fontSize: 14, color: '#F5EBDD',
  outline: 'none', fontFamily: 'inherit',
  width: '100%', boxSizing: 'border-box',
};

const lbl = { fontSize: 12, color: 'rgba(245,235,221,0.55)', marginBottom: 5, display: 'block', letterSpacing: '0.3px' };
const sel = { ...inp, background: '#2B2B1F', cursor: 'pointer', colorScheme: 'dark' };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };
const full = { gridColumn: '1 / -1' };
const sec = { fontSize: 11, fontWeight: 600, color: 'rgba(245,235,221,0.3)', textTransform: 'uppercase', letterSpacing: '1.2px', margin: '1.5rem 0 0.8rem', borderBottom: '0.5px solid rgba(245,235,221,0.06)', paddingBottom: 6 };

function F({ label, hint, children, full: isFull, error }) {
  return (
    <div style={isFull ? full : {}}>
      {error && <div style={{ fontSize: 11, color: '#E55D4A', marginBottom: 4 }}>⚠ {error}</div>}
      <label style={{ ...lbl, color: error ? '#E55D4A' : undefined }}>{label}{hint && <span style={{ fontWeight: 400, color: 'rgba(245,235,221,0.25)', marginLeft: 6 }}>{hint}</span>}</label>
      {children}
    </div>
  );
}

export default function PublicInfluencerForm() {
  const [searchParams] = useSearchParams();
  const platform = searchParams.get('platform') || 'ig';
  const token    = searchParams.get('token') || '';

  const [status, setStatus]       = useState('loading'); // loading | valid | invalid | submitted
  const [label, setLabel]         = useState('Chord Agency');
  const [form, setForm]           = useState(EMPTY);
  const [activeRates, setActiveRates] = useState([]);
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg]   = useState('');

  useEffect(() => {
    if (!token || !platform) { setStatus('invalid'); return; }
    verifyFormToken(platform, token)
      .then(data => { setLabel(data.label || 'Chord Agency'); setStatus('valid'); })
      .catch(() => setStatus('invalid'));
  }, [token, platform]);

  function set(k, v) {
    if (k === 'followers') {
      setForm(p => ({...p, [k]: v, tier: tierFromFollowers(v)}));
    } else {
      setForm(p => ({...p, [k]: v}));
    }
    if (errors[k]) setErrors(p => { const e={...p}; delete e[k]; return e; });
  }
  function toggleRate(rt) { setActiveRates(p => p.includes(rt) ? p.filter(x=>x!==rt) : [...p, rt]); }

  function validate() {
    const e = {};
    if (!form.influencer_name.trim()) e.influencer_name = 'Name is required';
    if (!form.email_contact.trim())   e.email_contact   = 'Email or contact is required';
    if (!form.niche)                  e.niche           = 'Please select a niche';
    if (form.email_contact && form.email_contact.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_contact))
      e.email_contact = 'Enter a valid email address';
    if (form.engagement_rate && parseFloat(form.engagement_rate) > 1)
      e.engagement_rate = 'Enter as decimal e.g. 0.045 for 4.5%';
    activeRates.forEach(rt => { if (!form[`rate_${rt}`]) e[`rate_${rt}`] = `Enter rate for ${rt}`; });
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true); setErrorMsg('');
    try {
      const payload = {...form};
      ['followers','avg_views','reach'].forEach(k => { payload[k] = payload[k] ? parseInt(payload[k]) : null; });
      ['engagement_rate','rate'].forEach(k => { payload[k] = payload[k] ? parseFloat(payload[k]) : null; });
      RATE_TYPES.forEach(k => {
        const key = `rate_${k}`;
        payload[key] = activeRates.includes(k) && payload[key] ? parseFloat(payload[key]) : null;
      });
      await publicSubmitInfluencer(platform, token, payload);
      setStatus('submitted');
    } catch(err) { setErrorMsg(err.message); }
    finally { setSubmitting(false); }
  }

  const platformLabel = { ig:'Instagram', moj:'Moj', snapchat:'Snapchat', reddit:'Reddit', yt:'YouTube', linkedin:'LinkedIn', twitter:'Twitter/X' }[platform] || platform;

  // Loading
  if (status === 'loading') return (
    <div style={{ minHeight:'100vh', background:'#1A1A1A', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'rgba(245,235,221,0.4)', fontSize:14 }}>Verifying link...</div>
    </div>
  );

  // Invalid
  if (status === 'invalid') return (
    <div style={{ minHeight:'100vh', background:'#1A1A1A', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
      <div style={{ textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:40, marginBottom:16 }}>🔗</div>
        <div style={{ fontSize:20, fontWeight:700, color:'#F5EBDD', marginBottom:8 }}>Invalid or Expired Link</div>
        <div style={{ fontSize:14, color:'rgba(245,235,221,0.45)' }}>This link is no longer valid. Please contact Chord Agency for a new link.</div>
      </div>
    </div>
  );

  // Submitted
  if (status === 'submitted') return (
    <div style={{ minHeight:'100vh', background:'#1A1A1A', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
      <div style={{ textAlign:'center', maxWidth:440 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
        <div style={{ fontSize:22, fontWeight:700, color:'#F5EBDD', marginBottom:8 }}>Thank you!</div>
        <div style={{ fontSize:14, color:'rgba(245,235,221,0.55)', lineHeight:1.7 }}>
          Your details have been submitted successfully to <strong style={{color:'#F5EBDD'}}>{label}</strong>. 
          We'll be in touch soon!
        </div>
        <div style={{ marginTop:24, fontSize:12, color:'rgba(245,235,221,0.3)' }}>Powered by Chord Influencer Hub</div>
      </div>
    </div>
  );

  // Form
  return (
    <div style={{ minHeight:'100vh', background:'#1A1A1A', color:'#F5EBDD', fontFamily:"'Montserrat', sans-serif", padding:'2rem 1rem' }}>
      {/* Top accent */}
      <div style={{ position:'fixed', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, #E55D4A, #F2951B, #D8E04D)` }} />

      <div style={{ maxWidth:680, margin:'0 auto' }}>
        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:'2rem', paddingTop:'1rem' }}>
          <div style={{ fontSize:22, fontWeight:700, letterSpacing:'-0.5px', marginBottom:4 }}>
            chord<span style={{color:'#E55D4A'}}>.</span>
          </div>
          <div style={{ fontSize:18, fontWeight:600, marginBottom:6 }}>{label}</div>
          <div style={{ fontSize:13, color:'rgba(245,235,221,0.45)' }}>
            {platformLabel} Influencer Submission Form
          </div>
          <div style={{ fontSize:12, color:'rgba(245,235,221,0.25)', marginTop:4 }}>
            Fill in your details below — takes about 2 minutes
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Basic */}
          <div style={sec}>Basic Info</div>
          <div style={grid}>
            <F label="Full Name *" error={errors.influencer_name}>
              <input style={{...inp, borderColor: errors.influencer_name ? '#E55D4A' : undefined}} value={form.influencer_name} onChange={e=>set('influencer_name',e.target.value)} placeholder="e.g. Priya Sharma" />
            </F>
            <F label={`${platformLabel} Handle / Profile URL`} error={errors.handle}>
              <input style={inp} value={form.handle} onChange={e=>set('handle',e.target.value)} placeholder={`https://${platform === 'ig' ? 'instagram' : platform}.com/...`} />
            </F>
            <F label="Tier" hint="auto-calculated from your followers">
              <div style={{
                ...inp,
                background: form.followers ? `${TIER_COLORS[tierFromFollowers(form.followers)]}18` : 'rgba(255,255,255,0.02)',
                borderColor: form.followers ? `${TIER_COLORS[tierFromFollowers(form.followers)]}60` : 'rgba(255,255,255,0.08)',
                color: form.followers ? TIER_COLORS[tierFromFollowers(form.followers)] : 'rgba(245,235,221,0.3)',
                cursor: 'not-allowed', fontWeight: 600,
              }}>
                {form.followers ? tierFromFollowers(form.followers) : '— enter followers count below first —'}
              </div>
            </F>
            <F label="Niche / Category *" error={errors.niche}>
              <select style={{...sel, borderColor: errors.niche ? '#E55D4A' : undefined}} value={form.niche} onChange={e=>set('niche',e.target.value)}>
                <option value="">Select niche</option>
                {NICHES.map(n=><option key={n}>{n}</option>)}
              </select>
            </F>
            <F label="Language(s)" hint="comma separated" full>
              <input style={inp} value={form.language} onChange={e=>set('language',e.target.value)} placeholder="Hindi, English" />
            </F>
            <F label="Audience Description" full>
              <input style={inp} value={form.audience} onChange={e=>set('audience',e.target.value)} placeholder="e.g. Young moms, tier 2 cities, 25-35 age group" />
            </F>
          </div>

          {/* Location */}
          <div style={sec}>Location</div>
          <div style={grid}>
            <F label="City"><input style={inp} value={form.city} onChange={e=>set('city',e.target.value)} placeholder="Mumbai" /></F>
            <F label="State"><input style={inp} value={form.state} onChange={e=>set('state',e.target.value)} placeholder="Maharashtra" /></F>
            <F label="Country"><input style={inp} value={form.country} onChange={e=>set('country',e.target.value)} placeholder="India" /></F>
          </div>

          {/* Metrics */}
          <div style={sec}>Your Metrics <span style={{fontSize:10,fontWeight:400,color:'rgba(245,235,221,0.25)'}}>— approximate is fine</span></div>
          <div style={grid}>
            <F label="Followers"><input style={inp} type="number" value={form.followers} onChange={e=>set('followers',e.target.value)} placeholder="e.g. 25000" /></F>
            <F label="Avg Views Per Post"><input style={inp} type="number" value={form.avg_views} onChange={e=>set('avg_views',e.target.value)} placeholder="e.g. 8000" /></F>
            <F label="Engagement Rate" hint="e.g. 0.045 = 4.5%" error={errors.engagement_rate}>
              <input style={{...inp, borderColor: errors.engagement_rate ? '#E55D4A' : undefined}} type="number" step="0.001" value={form.engagement_rate} onChange={e=>set('engagement_rate',e.target.value)} placeholder="e.g. 0.045" />
              {form.engagement_rate && !isNaN(form.engagement_rate) && !errors.engagement_rate && (
                <div style={{ fontSize:11, color:'#1D9E75', marginTop:4 }}>= {(parseFloat(form.engagement_rate)*100).toFixed(2)}%</div>
              )}
            </F>
          </div>

          {/* Rates */}
          <div style={sec}>Your Rates <span style={{fontSize:10,fontWeight:400,color:'rgba(245,235,221,0.25)'}}>— select what applies</span></div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {RATE_TYPES.map(rt => (
              <div key={rt}>
                <div style={{
                  display:'flex', alignItems:'center', gap:12,
                  background: activeRates.includes(rt) ? 'rgba(242,149,27,0.07)' : 'rgba(245,235,221,0.02)',
                  border: `0.5px solid ${errors[`rate_${rt}`] ? '#E55D4A' : activeRates.includes(rt) ? 'rgba(242,149,27,0.3)' : 'rgba(245,235,221,0.08)'}`,
                  borderRadius:8, padding:'10px 14px', transition:'all 0.15s',
                }}>
                  <input type="checkbox" id={`rate_${rt}`} checked={activeRates.includes(rt)} onChange={()=>toggleRate(rt)}
                    style={{ width:16, height:16, cursor:'pointer', accentColor:'#F2951B' }} />
                  <label htmlFor={`rate_${rt}`} style={{ fontSize:13, fontWeight:activeRates.includes(rt)?500:400, color:activeRates.includes(rt)?'#F5EBDD':'rgba(245,235,221,0.45)', cursor:'pointer', minWidth:80, textTransform:'capitalize' }}>{rt}</label>
                  {activeRates.includes(rt) && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
                      <span style={{ fontSize:12, color:'rgba(245,235,221,0.4)' }}>₹</span>
                      <input style={{...inp, width:140, padding:'7px 11px', fontSize:13, borderColor: errors[`rate_${rt}`] ? '#E55D4A' : undefined}}
                        type="number" value={form[`rate_${rt}`]} onChange={e=>set(`rate_${rt}`,e.target.value)} placeholder="Enter rate" />
                    </div>
                  )}
                </div>
                {errors[`rate_${rt}`] && <div style={{ fontSize:11, color:'#E55D4A', marginTop:3 }}>⚠ {errors[`rate_${rt}`]}</div>}
              </div>
            ))}
          </div>

          {/* Contact */}
          <div style={sec}>Contact & Collaboration</div>
          <div style={grid}>
            <F label="Email / WhatsApp *" error={errors.email_contact}>
              <input style={{...inp, borderColor: errors.email_contact ? '#E55D4A' : undefined}} value={form.email_contact} onChange={e=>set('email_contact',e.target.value)} placeholder="your@email.com or phone" />
            </F>
            <F label="Agency / Manager (if any)">
              <input style={inp} value={form.agency_manager} onChange={e=>set('agency_manager',e.target.value)} placeholder="Agency name" />
            </F>
            <F label="Preferred Deliverables" hint="comma separated" full>
              <input style={inp} value={form.preferred_deliverables} onChange={e=>set('preferred_deliverables',e.target.value)} placeholder="Reels, Stories, Static Post..." />
            </F>
            <F label="Past Work / Portfolio Links" full>
              <input style={inp} value={form.past_work} onChange={e=>set('past_work',e.target.value)} placeholder="Links to previous collaborations" />
            </F>
            <F label="Anything else you'd like us to know?" full>
              <input style={inp} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Optional notes..." />
            </F>
          </div>

          {errorMsg && (
            <div style={{ fontSize:13, color:'#E55D4A', marginTop:12, padding:'10px 14px', background:'rgba(229,93,74,0.1)', borderRadius:8, border:'0.5px solid rgba(229,93,74,0.3)' }}>
              ⚠ {errorMsg}
            </div>
          )}

          {Object.keys(errors).length > 0 && (
            <div style={{ fontSize:12, color:'#E55D4A', marginTop:10, padding:'8px 14px', background:'rgba(229,93,74,0.08)', borderRadius:8 }}>
              ⚠ Please fix {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''} above
            </div>
          )}

          <button type="submit" disabled={submitting} style={{
            width:'100%', marginTop:'1.5rem',
            background: submitting ? 'rgba(229,93,74,0.5)' : '#E55D4A',
            border:'none', borderRadius:10, padding:'13px',
            fontSize:15, fontWeight:700, color:'#fff',
            cursor: submitting ? 'not-allowed' : 'pointer', fontFamily:'inherit',
            letterSpacing:'0.3px',
          }}>
            {submitting ? 'Submitting...' : 'Submit My Details →'}
          </button>

          <div style={{ textAlign:'center', marginTop:'1rem', fontSize:11, color:'rgba(245,235,221,0.2)' }}>
            Your information will only be used by {label} for influencer partnership purposes.
          </div>
        </form>
      </div>
    </div>
  );
}
